import type { WatchlistItem, WatchlistList } from '../services/watchlist'

/** Mirrors Watchlist::MAX_LIST_DEPTH — the top level counts as 1. */
export const MAX_LIST_DEPTH = 3

export type ListDropPosition = 'before' | 'inside' | 'after'

export interface ListTreeRow {
  list: WatchlistList
  /** 0 at the top level. */
  depth: number
  hasChildren: boolean
}

function parentOf(list: WatchlistList): number | null {
  return list.parentId ?? null
}

/**
 * Depth-first rows (each parent followed by its sublists), keeping the
 * given order among siblings. Lists whose parent is missing go to the top.
 */
export function listTreeRows(lists: WatchlistList[]): ListTreeRow[] {
  const ids = new Set(lists.map(l => l.id))
  const children = new Map<number | null, WatchlistList[]>()
  for (const list of lists) {
    const p = parentOf(list)
    const key = p !== null && ids.has(p) && p !== list.id ? p : null
    children.set(key, [...(children.get(key) ?? []), list])
  }

  const rows: ListTreeRow[] = []
  const placed = new Set<number>()
  const walk = (list: WatchlistList, depth: number) => {
    if (placed.has(list.id)) return
    placed.add(list.id)
    const kids = children.get(list.id) ?? []
    rows.push({ list, depth, hasChildren: kids.length > 0 })
    for (const kid of kids) walk(kid, depth + 1)
  }
  for (const root of children.get(null) ?? []) walk(root, 0)
  // Anything left sits in a loop; show it at the top rather than lose it.
  for (const list of lists) walk({ ...list, parentId: null }, 0)
  return rows
}

/** Lists in tree order, as the backend returns them. */
export function orderListsAsTree(lists: WatchlistList[]): WatchlistList[] {
  return listTreeRows(lists).map(r => r.list)
}

/** The list itself and every list nested under it. */
export function listFamilyIds(
  lists: WatchlistList[],
  listId: number
): Set<number> {
  const out = new Set<number>([listId])
  let grew = true
  while (grew) {
    grew = false
    for (const list of lists) {
      const p = parentOf(list)
      if (p !== null && out.has(p) && !out.has(list.id)) {
        out.add(list.id)
        grew = true
      }
    }
  }
  return out
}

/** Unique items on a list or any of its sublists, in the lists' order. */
export function listFamilyItems(
  lists: WatchlistList[],
  listId: number
): WatchlistItem[] {
  const family = listFamilyIds(lists, listId)
  const seen = new Set<number>()
  const out: WatchlistItem[] = []
  for (const list of orderListsAsTree(lists)) {
    if (!family.has(list.id)) continue
    for (const item of list.items) {
      if (seen.has(item.id)) continue
      seen.add(item.id)
      out.push(item)
    }
  }
  return out
}

/** Parent chain from the top level down to (not including) the list. */
export function listAncestors(
  lists: WatchlistList[],
  listId: number
): WatchlistList[] {
  const byId = new Map(lists.map(l => [l.id, l]))
  const out: WatchlistList[] = []
  let current = byId.get(listId)
  while (current && parentOf(current) !== null) {
    const parent = byId.get(parentOf(current) as number)
    if (!parent || out.includes(parent) || parent.id === listId) break
    out.unshift(parent)
    current = parent
  }
  return out
}

function depthOf(lists: WatchlistList[], listId: number): number {
  return listAncestors(lists, listId).length
}

/** Levels in the list's subtree, counting itself (a leaf is 1). */
function subtreeHeight(lists: WatchlistList[], listId: number): number {
  const kids = lists.filter(l => parentOf(l) === listId && l.id !== listId)
  return 1 + Math.max(0, ...kids.map(k => subtreeHeight(lists, k.id)))
}

/**
 * Move a list (with its sublists) before, into (as last sublist) or after
 * another list. Returns the new lists in tree order with `parentId` and
 * `sortOrder` set, or null when the move is not allowed: onto itself or its
 * own sublists, or deeper than MAX_LIST_DEPTH. A null target with 'after'
 * means the end of the top level.
 */
export function moveList(
  lists: WatchlistList[],
  dragId: number,
  targetId: number | null,
  position: ListDropPosition
): WatchlistList[] | null {
  const dragged = lists.find(l => l.id === dragId)
  if (!dragged) return null

  let newParent: number | null
  if (targetId === null) {
    newParent = null
  } else {
    const target = lists.find(l => l.id === targetId)
    if (!target) return null
    if (listFamilyIds(lists, dragId).has(targetId)) return null
    newParent = position === 'inside' ? target.id : parentOf(target)
  }

  const newDepth = newParent === null ? 0 : depthOf(lists, newParent) + 1
  if (newDepth + subtreeHeight(lists, dragId) > MAX_LIST_DEPTH) return null

  // Sibling order under the new parent, without the dragged list.
  const ordered = orderListsAsTree(lists)
  const siblings = ordered.filter(
    l => parentOf(l) === newParent && l.id !== dragId
  )
  let index = siblings.length
  if (targetId !== null && position !== 'inside') {
    const at = siblings.findIndex(l => l.id === targetId)
    index = position === 'before' ? at : at + 1
  }
  const moved = { ...dragged, parentId: newParent }
  siblings.splice(index, 0, moved)

  const sortById = new Map<number, number>()
  siblings.forEach((l, i) => sortById.set(l.id, i))
  const next = ordered.map(l => {
    if (l.id === dragId)
      return { ...moved, sortOrder: sortById.get(l.id) as number }
    if (sortById.has(l.id))
      return { ...l, sortOrder: sortById.get(l.id) as number }
    return l
  })
  // Re-sort siblings by their new position, then flatten to tree order.
  const bySort = [...next].sort((a, b) => a.sortOrder - b.sortOrder)
  const result = orderListsAsTree(bySort)

  const unchanged =
    result.length === ordered.length &&
    result.every(
      (l, i) => l.id === ordered[i].id && parentOf(l) === parentOf(ordered[i])
    )
  return unchanged ? lists : result
}

/** Keyboard and menu moves, expressed as drops relative to a neighbour. */
export function listMoveTargets(lists: WatchlistList[], listId: number) {
  const ordered = orderListsAsTree(lists)
  const list = ordered.find(l => l.id === listId)
  if (!list) return {}
  const siblings = ordered.filter(l => parentOf(l) === parentOf(list))
  const at = siblings.findIndex(l => l.id === listId)
  const prev = siblings[at - 1]
  const next = siblings[at + 1]
  const parent = ordered.find(l => l.id === parentOf(list))

  const ok = (targetId: number | null, position: ListDropPosition) =>
    moveList(lists, listId, targetId, position) !== null
  return {
    up: prev && ok(prev.id, 'before') ? prev : undefined,
    down: next && ok(next.id, 'after') ? next : undefined,
    /** Becomes the last sublist of the list above it. */
    indent: prev && ok(prev.id, 'inside') ? prev : undefined,
    /** Lands right after its current parent. */
    outdent: parent && ok(parent.id, 'after') ? parent : undefined,
  }
}

/**
 * Which part of a row the pointer is over: the top quarter drops before,
 * the bottom quarter after, the middle into the list. An open list with
 * sublists has its sublists right below it, so its lower part means into
 * it. Without nesting, the row splits in two halves.
 */
export function dropPositionAt(
  offsetY: number,
  height: number,
  { canNest, expandedParent }: { canNest: boolean; expandedParent: boolean }
): ListDropPosition | null {
  const ratio =
    height > 0 && Number.isFinite(offsetY)
      ? Math.min(Math.max(offsetY / height, 0), 1)
      : 0.5
  if (!canNest) {
    if (ratio < 0.5) return 'before'
    return expandedParent ? null : 'after'
  }
  if (ratio < 0.25) return 'before'
  if (ratio > 0.75 && !expandedParent) return 'after'
  return 'inside'
}
